import { Link } from "react-router-dom";
import { Receipt, Users, Wallet, ShieldCheck, ArrowRight, CheckCircle, MapPin, TrendingUp, Building2, Globe } from "lucide-react";

const howItWorks = [
  { step: "01", title: "You set a monthly budget", desc: "Decide how much you want to reimburse each employee per month. You stay in full control of the cost." },
  { step: "02", title: "Employees buy their own plan", desc: "Your team picks an ACA-compliant individual health plan that actually fits their family, doctors, and budget." },
  { step: "03", title: "They submit proof", desc: "Employees submit proof of coverage and eligible expenses through a simple reimbursement process." },
  { step: "04", title: "You reimburse tax-free", desc: "Reimbursements are tax-free to both the employer and the employee. No payroll tax, no income tax on those dollars." },
];

const employerBenefits = [
  { icon: Wallet, title: "Predictable Costs", desc: "You set the allowance. No surprise premium hikes at renewal. Budget for benefits the way you budget for payroll." },
  { icon: Users, title: "Customize by Employee Class", desc: "Offer different allowances by full-time, part-time, salaried, hourly, location, or other approved categories. Eleven classes are allowed." },
  { icon: ShieldCheck, title: "No Group Plan Headaches", desc: "No participation requirements. No carrier negotiations. No employees stuck on a plan that does not fit them." },
  { icon: TrendingUp, title: "Scales as You Grow", desc: "ICHRA works whether you have three employees or three hundred. Add new hires without re-shopping your group plan." },
];

const bestFor = [
  { icon: Building2, label: "Small businesses that cannot afford traditional group health" },
  { icon: Globe, label: "Companies with remote employees across multiple states" },
  { icon: Users, label: "Businesses with a diverse mix of full-time, part-time, and contract workers" },
  { icon: TrendingUp, label: "Growing companies that want cost predictability" },
  { icon: Wallet, label: "Employers tired of double-digit premium increases every year" },
];

const StepCard = ({ item }) => (
  <div className="relative bg-white rounded-2xl p-7 border border-gray-100 shadow-sm hover:shadow-md transition-all">
    <span className="absolute top-5 right-5 font-bold text-4xl text-[#EAF9F9] leading-none select-none" style={{ fontFamily: "'Outfit', sans-serif" }}>
      {item.step}
    </span>
    <h3 className="font-bold text-lg text-[#1A2744] mb-2 pr-12" style={{ fontFamily: "'Outfit', sans-serif" }}>{item.title}</h3>
    <p className="text-[#475569] text-sm leading-relaxed">{item.desc}</p>
  </div>
);

const EmployerBenefitTile = ({ benefit }) => (
  <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-lg transition-all flex space-x-4">
    <div className="w-12 h-12 bg-[#EAF9F9] rounded-xl flex items-center justify-center flex-shrink-0">
      <benefit.icon size={22} className="text-[#2EC4C4]" />
    </div>
    <div>
      <h3 className="font-bold text-base text-[#1A2744] mb-1.5" style={{ fontFamily: "'Outfit', sans-serif" }}>{benefit.title}</h3>
      <p className="text-[#475569] text-sm leading-relaxed">{benefit.desc}</p>
    </div>
  </div>
);

export const ICHRAGuideSection = () => (
  <section data-testid="ichra-guide-section" className="py-20 bg-[#F8FAFC]">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="text-center mb-12 max-w-3xl mx-auto">
        <div className="inline-flex items-center space-x-2 bg-[#EAF9F9] text-[#2EC4C4] font-semibold text-xs uppercase tracking-wider px-4 py-2 rounded-full mb-5">
          <Receipt size={14} />
          <span>ICHRA Employer Guide</span>
        </div>
        <h2 className="font-bold text-3xl lg:text-4xl text-[#1A2744] mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
          A Smarter Alternative to Group Health Insurance
        </h2>
        <p className="text-[#475569] text-lg leading-relaxed">
          An Individual Coverage Health Reimbursement Arrangement (ICHRA) is a federal-approved benefit that lets you reimburse employees tax-free for their own individual health insurance. It's flexible, predictable, and especially powerful for small and growing businesses.
        </p>
      </div>

      {/* How It Works */}
      <div className="mb-16">
        <h3 className="font-bold text-2xl text-[#1A2744] mb-6 text-center" style={{ fontFamily: "'Outfit', sans-serif" }}>
          How ICHRA Works
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {howItWorks.map((item) => <StepCard key={item.step} item={item} />)}
        </div>
      </div>

      {/* Why Employers Choose ICHRA */}
      <div className="mb-16">
        <h3 className="font-bold text-2xl text-[#1A2744] mb-6 text-center" style={{ fontFamily: "'Outfit', sans-serif" }}>
          Why Employers Are Choosing ICHRA
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {employerBenefits.map((b) => <EmployerBenefitTile key={b.title} benefit={b} />)}
        </div>
      </div>

      {/* Best For */}
      <div className="bg-gradient-to-br from-[#1A2744] to-[#0F1B33] rounded-2xl p-8 lg:p-10">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-12 h-12 bg-[#2EC4C4]/15 border border-[#2EC4C4]/30 rounded-xl flex items-center justify-center">
            <CheckCircle size={22} className="text-[#2EC4C4]" />
          </div>
          <div>
            <p className="text-[#2EC4C4] text-xs font-bold uppercase tracking-wider">Best Fit For</p>
            <h3 className="font-bold text-2xl text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Is ICHRA Right for Your Business?
            </h3>
          </div>
        </div>
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {bestFor.map((item) => (
            <li key={item.label} className="flex items-start space-x-3 text-white">
              <item.icon size={18} className="text-[#2EC4C4] flex-shrink-0 mt-1" />
              <span className="text-gray-200 text-sm leading-relaxed">{item.label}</span>
            </li>
          ))}
        </ul>
        <div className="mt-8 pt-8 border-t border-white/10 text-center">
          <p className="text-gray-300 text-base mb-4">
            Not sure if ICHRA makes sense for your team? Let's walk through your numbers together.
          </p>
          <Link
            to="/contact"
            data-testid="ichra-cta-btn"
            className="inline-flex items-center space-x-2 bg-[#2EC4C4] hover:bg-[#25A0A0] text-white font-semibold rounded-lg px-6 py-3 transition-all shadow-md hover:shadow-lg active:scale-95"
          >
            <span>Get a Free ICHRA Consultation</span>
            <ArrowRight size={18} />
          </Link>
        </div>
      </div>
    </div>
  </section>
);
